import React, { useEffect, useState } from "react";
import AppMenu from "../components/AppMenu";
import { Table } from "reactstrap";
import { getStatesById } from "../services/stateAPI";
import { getSiteData } from "../services/siteAPI";
import moment from "moment-timezone";
import { useParams } from "react-router";
import { title } from "../config";
import { BackButton } from "../components/BackButton";
import { useToast } from "../components/ToastContext";
import Pagination from "../components/Pagination";
import "../assets/css/Table.css";

const SiteLog = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { id } = useParams();

  const reloadSite = async (siteId) => {
    try {
      setLoading(true);
      let data;

      if (siteId !== undefined) {
        data = await getStatesById(siteId, showToast);
      } else {
        data = await getSiteData(showToast);
      }

      setAllSites(Array.isArray(data) ? data : []);
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setItems([]);
      setAllSites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadSite(id);
  }, [id]);

  const applyStatusFilter = (filterValue, baseList = allSites) => {
    let result = baseList;

    if (filterValue === "success") {
      result = baseList.filter((record) => record.runningStatus === true);
    } else if (filterValue === "error") {
      result = baseList.filter((record) => record.runningStatus === false);
    }

    setItems(result);
  };

  const onSearchChange = (e) => {
    const value = e?.target?.value || "";
    const text = value.toLowerCase();

    setSearchText(value);

    if (value.length) {
      const statusMap = { true: "Active", false: "Inactive" };
      const runningStatusMap = { true: "SUCCESS", false: "ERROR" };

      const searchResult = allSites.filter((record) => {
        const nameMatch = record.name?.toLowerCase().includes(text);
        const linkMatch = record.link?.toLowerCase().includes(text);

        let hostnameMatch = false;
        if (record.link) {
          try {
            const hostname = new URL(record.link).hostname.toLowerCase();
            hostnameMatch = hostname.includes(text);
          } catch {
            hostnameMatch = false;
          }
        }

        const statusString =
          typeof record.status === "boolean"
            ? statusMap[record.status]
            : (record.status || "").toString();

        const runningStatusString =
          typeof record.runningStatus === "boolean"
            ? runningStatusMap[record.runningStatus]
            : (record.runningStatus || "").toString();

        return (
          nameMatch ||
          linkMatch ||
          hostnameMatch ||
          statusString.toLowerCase().includes(text) ||
          runningStatusString.toLowerCase().includes(text)
        );
      });

      setItems(searchResult);
    } else {
      setItems(allSites);
    }
  };

  const handleStatusFilterChange = (e) => {
    const selected = e.target.value;
    setStatusFilter(selected);
    applyStatusFilter(selected);
  };

  return (
    <div>
      <div className="px-2 sticky-header">
        <h3 className="m-0 p-2">{title} | Details</h3>
        <AppMenu title="details" />
      </div>

      <div className="p-0 px-2 m-0">
        <div className="site-toolbar px-2">
          <div className="site-toolbar-left">
            <BackButton icon="←" />
            <input
              value={searchText}
              onChange={onSearchChange}
              placeholder="Search Site Name"
              type="search"
              className="site-search"
            />

            <div className="site-filter-wrapper">
              <div
                className={`site-filter-display ${statusFilter ? "active" : ""}`}
                onClick={() =>
                  document
                    .getElementById("statusSelect")
                    .classList.toggle("open")
                }
              >
                <span>{statusFilter ? statusFilter.toUpperCase() : "All"}</span>

                {statusFilter && (
                  <span
                    className="filter-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusFilter("");
                      setItems(allSites);
                      document
                        .getElementById("statusSelect")
                        .classList.remove("open");
                    }}
                  >
                    ×
                  </span>
                )}
                <span className="filter-arrow">▾</span>
              </div>

              <div id="statusSelect" className="site-filter-dropdown">
                <div
                  onClick={() => {
                    handleStatusFilterChange({ target: { value: "" } });
                    document
                      .getElementById("statusSelect")
                      .classList.remove("open");
                  }}
                >
                  All
                </div>
                <div
                  onClick={() => {
                    handleStatusFilterChange({ target: { value: "success" } });
                    document
                      .getElementById("statusSelect")
                      .classList.remove("open");
                  }}
                >
                  SUCCESS
                </div>
                <div
                  onClick={() => {
                    handleStatusFilterChange({ target: { value: "error" } });
                    document
                      .getElementById("statusSelect")
                      .classList.remove("open");
                  }}
                >
                  ERROR
                </div>
              </div>
            </div>
          </div>
          <span className="site-total">Total: {items.length}</span>
        </div>
      </div>

      <StateList items={Array.isArray(items) ? items : []} loading={loading} />
    </div>
  );
};

const StateList = ({ items = [], loading }) => {
  const [localItems, setLocalItems] = useState([]);
  const [toggleSorting, setToggleSorting] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    // Always ensure it's an array
    setLocalItems(Array.isArray(items) ? [...items] : []);
  }, [items]);


  const totalPages = Math.ceil(localItems.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleItems = localItems.slice(startIndex, startIndex + pageSize);

  const sortTable = (field) => {
    if (!localItems.length) return;

    const sorted = [...localItems].sort((a, b) => {
      if (a[field] < b[field]) return toggleSorting ? -1 : 1;
      if (a[field] > b[field]) return toggleSorting ? 1 : -1;
      return 0;
    });

    setLocalItems(sorted);
    setToggleSorting(toggleSorting ? 0 : 1);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "75vh",
        }}
      >
        <div className="spinner-border">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="site-table-container" style={{ paddingBottom: "calc(40px + 2rem)" }}>
      <Table className="site-table">
        <thead>
          <tr>
            <th>Sr. No.</th>
            <th onClick={() => sortTable("name")}>Name</th>
            <th onClick={() => sortTable("link")}>Link</th>
            <th onClick={() => sortTable("sslExpiry")}>SSL Expiry</th>
            <th onClick={() => sortTable("domainExpiry")}>Domain Expiry</th>
            <th onClick={() => sortTable("runningStatus")}>Running Status</th>
            <th onClick={() => sortTable("status")}>Site Status</th>
            <th onClick={() => sortTable("time")}>Time</th>
            <th onClick={() => sortTable("cpuLoad")}>CPU-Usage</th>
            <th onClick={() => sortTable("exec_mode")}>Exec Mode</th>
            <th onClick={() => sortTable("freeMem")}>Free M / Total M</th>
          </tr>
        </thead>

        <tbody>
          {localItems.length === 0 ? (
            <tr>
              <td colSpan="11" className="text-center py-3">
                No data available
              </td>
            </tr>
          ) : (
            visibleItems.map((item, index) => (
              <tr key={item._id || item.name}>
                <td>{startIndex + index + 1}</td>
                <td>{item.name}</td>
                <td>
                  {item.link ? (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={item.link}
                    >
                      {new URL(item.link).hostname}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {item.sslExpiry && item.sslExpiry !== "NO_CERT_INFO"
                    ? moment(item.sslExpiry).format("DD/MM/YYYY")
                    : "-"}
                </td>
                <td>
                  {item.domainExpiry
                    ? moment(item.domainExpiry).format("DD/MM/YYYY")
                    : "-"}
                </td>
                <td>
                  {item.runningStatus === true ? (
                    <span className="text-success">SUCCESS</span>
                  ) : item.runningStatus === false ? (
                    <span className="text-danger">ERROR</span>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {item.status === true ? (
                    <span className="text-success">Active</span>
                  ) : item.status === false ? (
                    <span className="text-danger">Inactive</span>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {item.time
                    ? moment(item.time)
                      .tz("Asia/Kolkata")
                      .format("hh:mm A - DD/MM/YYYY")
                    : "-"}
                </td>
                <td>{item.cpuLoad ? item.cpuLoad.join(" / ") : "-"}</td>
                <td>{item.exec_mode || "-"}</td>
                <td>
                  {item.freeMem !== undefined && item.totalMem ? (
                    (() => {
                      const ramUsedPercent = item.ramUsagePercent !== undefined 
                        ? item.ramUsagePercent 
                        : Math.round(((item.totalMem - item.freeMem) / item.totalMem) * 100);
                      const isHighRam = ramUsedPercent >= 80;
                      return (
                        <span>
                          {item.freeMem} / {item.totalMem}
                          <span className={`badge ms-2 ${isHighRam ? 'bg-danger text-white' : 'bg-secondary text-light'}`} style={{ fontSize: '0.75rem' }}>
                            {ramUsedPercent}% used
                          </span>
                        </span>
                      );
                    })()
                  ) : "-"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default SiteLog;